<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/store/user';
import { useDisplay } from 'vuetify';
import { useRouter } from 'vue-router';
import { useStation } from '@/store/station';
// import { until } from '@vueuse/core';

const user = useUserStore();
const { t } = useI18n();
const router = useRouter();
const station = useStation();
const { name } = useDisplay();

type LoginForm = { username: string | null; password: string | null; };

const is_password_shown = ref<boolean>(false);
const valid = ref<boolean>(true);
const login_form = ref<LoginForm>({ username: null, password: null });
const login_pending = ref<boolean>(false);
const login_status = ref<number | null>(null);

const rules = {
  username_rules: [
    (v: string | null): boolean | string => !!v || t("username"),
  ],
  password_rules: [
    (v: string | null): boolean | string => !!v || t("password")
  ]
};

const card_width = computed((): string => {
  switch (name.value) {
    case "xs":
      return "w-100";
    case "sm":
      return "w-100";
    default:
      return "w-50";
  }
});
const card_action_location = computed((): string => {
  switch (name.value) {
    case "xs":
    case "sm":
      return "justify-center";
    default:
      return "justify-end";
  }
});
const show_error = computed((): boolean => {
  return login_status.value !== null && (login_status.value < 200 || login_status.value >= 300);
});
const error_message = computed((): string => {
  const code: number = login_status.value!;
  if (code === 408) return t("timeout") as string;
  else if (code === 401) return t("wrong_credentials") as string;
  return `Connection error (error code ${code})`;
  // const message: string = login_store.msg_from_backend || "";
  // return `${message} (status code ${code})`;
});

async function submit() {
  login_pending.value = true;
  try {
    const login_result = await user.do_login(
      login_form.value.username?.trim() || "",
      login_form.value.password?.trim() || "",
      station.qrcode || -1
    );
    const logged = login_result == 200;
    login_status.value = login_result;
    login_pending.value = false;

    if (! logged) {
      console.log("[user-access.submit]: can't log in (API request failed)");
      return;
    }

    const qrcode = station.qrcode;
    if (qrcode !== null && qrcode > 0) {
      await router.push(`/${qrcode}`);
    } else {
      await router.push("/");
    }
  } catch (err) {
    login_pending.value = false;
    console.error("[user-access.submit]: can't log in (exception): ", err);
  }
}
</script>

<template>
  <v-container :class="card_width" fluid>
    <v-row class="justify-center">
      <v-col>
        <v-card elevation="10">
          <v-card-title class="py-1 bg-primary font-weight-semibold">
            {{ t("title") }}
          </v-card-title>
          <v-divider />
          <v-card-text class="py-0">
            <v-form v-model="valid" validate-on="lazy" @submit.prevent="submit">
              <v-text-field
                v-model="login_form.username"
                variant="underlined"
                class="py-0 mt-5"
                prepend-icon="mdi-account"
                :label="t('username')"
                :rules="rules.username_rules"
                hide-details
              />
              <v-text-field
                v-model="login_form.password"
                variant="underlined"
                class="pt-5 pb-2"
                prepend-icon="mdi-lock"
                :label="t('password')"
                :rules="rules.password_rules"
                :type="is_password_shown ? 'text' : 'password'"
                :append-inner-icon="is_password_shown ? 'mdi-eye' : 'mdi-eye-off'"
                hide-details
                @click:append-inner="is_password_shown = !is_password_shown"
              />
              <v-alert class="caption" :model-value="show_error" type="error" variant="tonal">
                {{ error_message }}
              </v-alert>
              <v-row :class="`px-2 py-2 ${card_action_location}`">
                <v-btn
                  class="mt-3 mb-3"
                  color="success"
                  type="submit"
                  :loading="login_pending"
                  :disabled="login_pending"
                >
                  {{ t("login_action") }}
                </v-btn>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<i18n lang="yaml">
en:
  title: "Login"
  username: "Username"
  password: "Password"
  login_action: "Login"
  validation:
    username: "Username is required"
    password: "Password is required"
  timeout: "Cannot reach our servers: check your internet connection"
  wrong_credentials: "Wrong username or password"
it:
  title: "Accesso utente"
  username: "Nome utilizzatore"
  password: "Password"
  login_action: "Login"
  validation:
    username: "È richiesto un username per poter accedere"
    password: "È richiesta una password per poter accedere"
  timeout: "Impossibile contattare i server: controlla la tua connessione ad internet"
  wrong_credentials: "Username o password errati"
fr:
  title: "Connexion privée"
  username: "Nom d'utilisateur"
  password: "Mot de passe"
  login_action: "Se connecter"
  validation:
    username: "Le nom d'utilisateur est requis"
    password: "Le mot de passe est requis"
  timeout: "Ne peut pas atteindre nos serveurs, vérifiez votre connexion internet"
  wrong_credentials: "Mauvais nom d'utilisateur/mot de passe"
de:
  title: "Zugang des benützers"
  username: "Name des benützers"
  password: "Password"
  login_action: "Einloggen"
  validation:
    username: "Überprüfen des benutzers"
    password: "Überprüfen des password"
  timeout: "Impossibile contattare i server: controlla la tua connessione ad internet"
  wrong_credentials: "Name oder Passwort Nicht richtig"
nl:
  title: "Verbinding"
  username: "Gebruikersnaam"
  password: "Wachtwoord"
  login_action: "Aanmelden"
  validation:
    username: "De gebruikersnaam is vereist."
    password: "Het wachtwoord is vereist."
  timeout: "Kan onze servers niet bereiken, controleer uw internetverbinding"
  wrong_credentials: "Verkeerde gebruikersnaam/wachtwoord"
</i18n>

<style scoped lang="scss">
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
